import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import { useEstadoContabilidad } from '@/dominio/contabilidad/useContabilidad'
import { EncabezadoPantalla } from './-pantalla'
import { Secciones } from './-compras/comun'
import { RegistroActivos } from './-activos/Registro'
import { CategoriasActivo } from './-activos/Categorias'
import { DepreciacionContableYFiscal } from './-activos/Depreciacion'

/**
 * Activos fijos (`docs/adr/0028` §8), del complemento de contabilidad: el
 * registro, sus categorías y la depreciación, contable y fiscal por separado.
 */

const SECCIONES = [
  { id: 'registro', etiqueta: 'Registro' },
  { id: 'categorias', etiqueta: 'Categorías' },
  { id: 'depreciacion', etiqueta: 'Depreciación' },
] as const

type IdSeccion = (typeof SECCIONES)[number]['id']

export const Route = createFileRoute('/activos-fijos')({
  validateSearch: (b: Record<string, unknown>): { seccion?: IdSeccion } => ({
    seccion: SECCIONES.some((s) => s.id === b.seccion) && b.seccion !== 'registro' ? (b.seccion as IdSeccion) : undefined,
  }),
  component: PantallaActivos,
})

function PantallaActivos() {
  const estado = useEstadoContabilidad()
  const navegar = useNavigate({ from: Route.fullPath })
  const seccion = Route.useSearch().seccion ?? 'registro'

  if (estado.isLoading) return <LineaEsqueleto ancho="w-1/2" />

  if (!estado.data?.contratada) {
    return (
      <div className="flex max-w-[900px] flex-col gap-4">
        <EncabezadoPantalla titulo="Activos fijos" />
        <EstadoVacio
          variante="primera-vez"
          mensaje="Los activos fijos van con el complemento de contabilidad: el registro, la depreciación de cada mes con su asiento y la fiscal del art. 287. Para encenderlo, hable con Agentical Group."
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Activos fijos"
        descripcion="Lo que la empresa usa por más de un año. Se deprecia solo cada mes en línea recta; la depreciación fiscal del art. 287 va aparte."
      />
      <Secciones
        etiqueta="Secciones de activos fijos"
        secciones={SECCIONES}
        actual={seccion}
        irA={(s) => void navegar({ search: { seccion: s === 'registro' ? undefined : s }, replace: true })}
      />
      {seccion === 'registro' ? <RegistroActivos /> : null}
      {seccion === 'categorias' ? <CategoriasActivo /> : null}
      {seccion === 'depreciacion' ? <DepreciacionContableYFiscal /> : null}
    </div>
  )
}
